// import "./App.css";
import { useEffect, useState } from "react";
import Simulator from "./opencv/Simulator";
import * as fr from "./translations/fr.json";
import * as de from "./translations/de.json";
import * as it from "./translations/it.json";
import * as es from "./translations/es.json";
import countries from "./assets/countries.json";
import { detectLanguage } from "./helpers/language";

const API_URL = "https://eyes-recoloring-simulator-api.helvy.ch/submit";
const API_KEY = "QH1o69m6H1O5KQ1m6H1O5KQ1m6H1O5";

// Mapping from country codes to the exact names in countries.json
const countryCodeToName = {
  FR: "France",
  DE: "Germany",
  IT: "Italy",
  ES: "Spain",
  GB: "United Kingdom of Great Britain and Northern Ireland (the)",
  US: "United States of America (the)",
  CH: "Switzerland",
  BE: "Belgium",
  NL: "Netherlands (the)",
  PT: "Portugal",
  AT: "Austria",
  CA: "Canada",
  AU: "Australia",
  BR: "Brazil",
  MX: "Mexico",
  JP: "Japan",
  CN: "China",
  IN: "India",
  RU: "Russian Federation (the)",
  PL: "Poland",
  SE: "Sweden",
  NO: "Norway",
  DK: "Denmark",
  FI: "Finland",
  IE: "Ireland",
  GR: "Greece",
  CZ: "Czechia",
  RO: "Romania",
  HU: "Hungary",
  MA: "Morocco",
  DZ: "Algeria",
  TN: "Tunisia",
  EG: "Egypt",
  SA: "Saudi Arabia",
  AE: "United Arab Emirates (the)",
  LB: "Lebanon",
  TR: "Turkey",
  IL: "Israel",
  SN: "Senegal",
  CI: "Côte d'Ivoire",
};

function App() {
  const [language, setLanguage] = useState("fr");
  const [showSimulator, setShowSimulator] = useState(localStorage.getItem("simulator-form-submitted") == "1");
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    country: "France", // Default to France
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const t = (str) => {
    switch (language) {
      case "fr":
        return fr[str];
      case "de":
        return de[str];
      case "it":
        return it[str];
      case "es":
        return es[str];
      default:
        return str;
    }
  };

  useEffect(() => {
    setLanguage(detectLanguage());
  }, []);

  // Detect user's country from IP address
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const response = await fetch("https://ipapi.co/json/");
        if (response.ok) {
          const data = await response.json();
          const countryCode = data.country_code;
          const detectedCountry = countryCodeToName[countryCode];
          
          // Only update if we have a valid mapping for this country
          if (detectedCountry && countries.includes(detectedCountry)) {
            setFormData((prev) => ({
              ...prev,
              country: detectedCountry,
            }));
          }
          // Otherwise keep France as default
        }
      } catch (error) {
        // If detection fails, keep France as default
        console.log("Country detection failed, using France as default");
      }
    };

    detectCountry();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          k: API_KEY,
          firstname: formData.firstname,
          lastname: formData.lastname,
          email: formData.email,
          country: formData.country,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to submit form");
      }

      // Success - save to localStorage and show simulator
      localStorage.setItem("simulator-form-submitted", "1");
      setTimeout(() => {
        setShowSimulator(true);
      }, 500);
    } catch (err) {
      console.error("Form submission error:", err);
      setError(t("An error has occurred. Please try again later."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {showSimulator && <Simulator isBlurred={!showSimulator} />}

      {!showSimulator && <>
        <div
          className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 outline-none focus:outline-none py-4"
          style={{ zIndex: 1200 }}
        >
          <div className="relative w-auto mx-4 my-auto" style={{ width: '95%', maxWidth: '900px' }}>
            {/*content*/}
            <div className="border-0 shadow-lg relative flex flex-col w-full bg-white rounded-md outline-none focus:outline-none" style={{ maxHeight: '85vh' }}>
              {/*header*/}
              <div className="flex items-start justify-between py-4 px-6 sticky top-0 bg-white z-10 border-b flex-shrink-0">
                <div className="text-center w-full">
                  <h3 className="text-xl font-semibold">
                    {t("Discover your new face")}
                  </h3>
                  <h5 className="text-sm font-light">
                    {t("Fill out the form below to access New Eyes Paris simulator and try our colors for free.")}
                  </h5>
                </div>
              </div>

              {/*body*/}
              <div className="relative px-6 py-4 overflow-y-auto flex-1" style={{ minHeight: 0 }}>
                <p className="my-4 text-blueGray-500 text-sm leading-relaxed">
                  {t("Actual results may differ from simulations due to healing and other factors.")}
                  <br />
                  <strong>{t("To use the simulator, allow neweyesparis.com to use your camera.")}</strong>
                </p>
                
                {/* Custom Lead Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstname" className="block text-sm font-medium text-gray-700 mb-1">
                        {t("First name")} *
                      </label>
                      <input
                        type="text"
                        id="firstname"
                        name="firstname"
                        value={formData.firstname}
                        onChange={handleInputChange}
                        required
                        autoFocus
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="lastname" className="block text-sm font-medium text-gray-700 mb-1">
                        {t("Last name")} *
                      </label>
                      <input
                        type="text"
                        id="lastname"
                        name="lastname"
                        value={formData.lastname}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      {t("Email")} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                      {t("Country")} *
                    </label>
                    <select
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="">{t("Select a country")}</option>
                      {countries.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                  </div>

                  {error && (
                    <div className="text-red-600 text-sm">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3 px-4 bg-black hover:bg-gray-800 disabled:bg-gray-500 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                  >
                    {isSubmitting ? t("Loading...") : t("Access the simulator")}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div className="opacity-25 fixed inset-0 z-40 bg-black" />
      </>}
    </>
  );
}

export default App;
